const BASE = '/api';
const TOKEN_KEY = 'tachoparser_token';

function getAuthHeaders() {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request(url, options = {}) {
  const headers = { ...getAuthHeaders(), ...options.headers };
  const res = await fetch(url, { ...options, headers });

  if (res.status === 401) {
    // Token expired or invalid — clear auth and reload
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('tachoparser_user');
    window.location.reload();
    throw new Error('Session expired');
  }

  if (!res.ok) {
    let message = `Request failed: ${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body.detail) message = body.detail;
    } catch {
      // ignore parse errors
    }
    // The status goes with the words, so a caller can tell a refusal of
    // what it sent (a 4xx) from a server or a network that failed.
    const e = new Error(message);
    e.status = res.status;
    throw e;
  }
  return res;
}

async function getJson(path) {
  const res = await request(`${BASE}${path}`);
  return res.json();
}

async function putJson(path, data) {
  const res = await request(`${BASE}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

async function postJson(path, data) {
  const res = await request(`${BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

async function deleteJson(path) {
  const res = await request(`${BASE}${path}`, { method: 'DELETE' });
  return res.json();
}

async function patchJson(path, data) {
  const res = await request(`${BASE}${path}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

/**
 * GET /api/dashboard
 */
export async function fetchDashboard() {
  return getJson('/dashboard');
}

/**
 * POST /api/upload-fast (multipart FormData) — Parse only, no analysis.
 * Much faster than full upload. Call triggerAnalysis() after all files uploaded.
 * @param {FileList|File[]} files
 */
export async function uploadFiles(files) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  const res = await request(`${BASE}/upload-fast`, {
    method: 'POST',
    body: formData,
  });
  return res.json();
}

/**
 * POST /api/analyse — Trigger background analysis on all unanalysed files.
 */
export async function triggerAnalysis() {
  return postJson('/analyse');
}

/**
 * GET /api/analyse/status — live parse/analyse progress for the caller's org.
 * Poll this while stage !== 'ready' to drive a "parsing X/Y" indicator.
 * @returns {{ stage: 'ready'|'analysing'|'idle', files_done: number,
 *             files_total: number, pending: number }}
 */
export async function fetchAnalyseStatus() {
  return getJson('/analyse/status');
}

/**
 * Upload a single file. Used by the progress-tracking upload flow.
 * @param {File} file
 * @returns {{ processed: [...], errors: [...] }}
 */
export async function uploadSingleFile(file) {
  const formData = new FormData();
  formData.append('files', file);
  const res = await request(`${BASE}/upload-fast`, {
    method: 'POST',
    body: formData,
  });
  return res.json();
}

/**
 * Upload a batch of files in one request.
 * @param {File[]} files
 * @returns {{ processed: [...], errors: [...] }}
 */
export async function uploadBatch(files) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  const res = await request(`${BASE}/upload-fast`, {
    method: 'POST',
    body: formData,
  });
  return res.json();
}

/**
 * GET /api/drivers
 */
export async function fetchDrivers() {
  return getJson('/drivers');
}

/**
 * F6 MVP — driver employment-window endpoints
 */
export async function fetchDriverEmploymentInfo(driverName) {
  return getJson(`/drivers/${encodeURIComponent(driverName)}/employment-info`);
}

export async function updateDriverEmploymentDates(driverName, { start, end }) {
  return patchJson(
    `/drivers/${encodeURIComponent(driverName)}/employment-dates`,
    { start, end },
  );
}

export async function fetchOnboardingStatus() {
  return getJson('/drivers/onboarding-status');
}

/**
 * GET /api/activities?driver=&date_from=&date_to=&vehicle=
 */
export async function fetchActivities(params = {}) {
  const query = buildQuery(params);
  return getJson(`/activities${query}`);
}

/**
 * GET /api/infringements?driver=&type=&date_from=&date_to=
 */
export async function fetchInfringements(params = {}) {
  const query = buildQuery(params);
  return getJson(`/infringements${query}`);
}

/**
 * GET /api/compliance-report — the Driver Compliance Report union (RPT-P1).
 * Folds each driver's infringements, speeding and harsh-braking into ONE
 * weighted risk score (server-side, single source of truth), org-scoped and
 * sorted riskiest-first.
 * @param {Object} [params] { date_from, date_to, driver }
 * @returns {{ period: Object, weights: Object, drivers: Array, totals: Object }}
 */
export async function fetchComplianceReport(params = {}) {
  const query = buildQuery(params);
  return getJson(`/compliance-report${query}`);
}

/**
 * GET /api/infringements/summary — the dashboard's three send-pipeline
 * tile counts. Canonical, not-closed rows only.
 * @returns {{ new_unsent_7d: number, total_unsent: number,
 *             awaiting_signature: number }}
 */
export async function fetchInfringementsSummary() {
  return getJson('/infringements/summary');
}

/**
 * GET /api/mileage — the Missing-Mileage list.
 *
 * OWNED scope (2026-07-02): the backend returns ONLY vehicles the org owns —
 * ACTIVE vehicles by default, plus the org's ARCHIVED vehicles when
 * `includeArchived` is set. Vehicles that never belonged to the org are never
 * returned. Orgs with no ownership data (not yet onboarded) see everything
 * unchanged (backend fail-open).
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.showAll] when true, append ?show_all=true to return
 *   every gap (incl. sub-5-minute yard shuffles and card-gap rows with NULL
 *   minutes). When false (default), the backend returns only confirmed no-card
 *   gaps with no_card_minutes >= 5.
 * @param {boolean} [opts.includeArchived] when true, also return gaps on the
 *   org's ARCHIVED vehicles (default false = active fleet only).
 */
export async function fetchMileage({ showAll = false, includeArchived = false } = {}) {
  const query = buildQuery({
    show_all: showAll ? 'true' : '',
    include_archived: includeArchived ? 'true' : '',
  });
  return getJson(`/mileage${query}`);
}

/**
 * GET /api/mileage/summary — fleet missing-mileage rollup for the
 * dashboard tile. Delegates server-side to the same row assembly as
 * /api/mileage, so the visibility semantics (canonical-only, 5-minute
 * yard-shuffle filter) are identical to the Missing Mileage tab.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.showAll] mirror of fetchMileage's showAll.
 * @param {boolean} [opts.includeArchived] mirror of fetchMileage's
 *   includeArchived — pass the same value so the tile matches the table.
 * @returns {{ total_km: number,
 *             vehicles: Array<{registration: string, total_km: number,
 *                              open_events: number, latest_date: ?string}> }}
 *   vehicles is worst-first and contains ONLY vehicles with gaps.
 */
export async function fetchMileageSummary({ showAll = false, includeArchived = false } = {}) {
  const query = buildQuery({
    show_all: showAll ? 'true' : '',
    include_archived: includeArchived ? 'true' : '',
  });
  return getJson(`/mileage/summary${query}`);
}

/**
 * GET /api/mileage/not-assessable — VU-absence design item 3 (2026-08-18).
 * The org's ACTIVE vehicles that were card-DRIVEN inside the assessed window
 * (default 60 days) but have NO VU coverage over it — the vehicles for which
 * unaccounted mileage is NOT COMPUTABLE. Without this list they render as
 * BLANK on the Unaccounted Mileage tab, which a TM reads as "nothing wrong";
 * a vehicle driven with no card inserted looks identical to a quiet one.
 *
 * @returns {Promise<{window_days:number, window_start:string,
 *   window_end:string,
 *   not_assessable: ?Array<{registration:string, window_start:string,
 *     window_end:string, last_vu_coverage:?string, driving_days:number,
 *     last_driving:?string}>}>}
 *   not_assessable is null (not []) when the backend lookup failed — the
 *   caller must render NOTHING rather than a claim in either direction.
 */
export async function fetchMileageNotAssessable() {
  return getJson('/mileage/not-assessable');
}

/**
 * GET /api/mileage/ledger — the per-vehicle/day reconciliation ledger
 * (Sprint C). Org-scoped day rows, newest first. Each row reconciles one
 * vehicle-day (total = card + no_card + unexplained for coverage
 * 'vu'/'vu_partial') or carries a 'not_uploaded' span (Date..span_end_date,
 * total only). `drivers` arrives as a parsed array; closure/assignment
 * state is joined as closure_status / closure_reason / closed_by /
 * closed_at / assigned_driver_name.
 *
 * OWNED scope (2026-07-02): mirrors /api/mileage — never-owned vehicles'
 * rows are never returned; archived org vehicles only with includeArchived.
 *
 * @param {Object} [opts]
 * @param {string} [opts.vehicle] exact registration filter
 * @param {string} [opts.dateFrom] inclusive YYYY-MM-DD lower bound
 * @param {string} [opts.dateTo] inclusive YYYY-MM-DD upper bound
 * @param {boolean} [opts.includeArchived] also return the org's ARCHIVED
 *   vehicles' day rows (default false = active fleet only)
 */
export async function fetchMileageLedger({ vehicle, dateFrom, dateTo, includeArchived = false } = {}) {
  const query = buildQuery({
    vehicle,
    date_from: dateFrom,
    date_to: dateTo,
    include_archived: includeArchived ? 'true' : '',
  });
  return getJson(`/mileage/ledger${query}`);
}

/**
 * GET /api/card-mileage-gaps — Missing Vehicle Mileage (Tachomaster method).
 *
 * Vehicle-centric odometer chain across ALL drivers' cards: per vehicle, a
 * positive jump between one card session's CLOSING odometer and the next
 * session's OPENING odometer is mileage driven with no card inserted —
 * regardless of driver or how many days apart (garage/PMI moves, yard shunts).
 * This is a DISTINCT surface from /api/mileage (the no-card / not-uploaded
 * day ledger); it needs no VU file at all.
 *
 * OWNED scope (2026-07-02): always fleet-scoped — the backend validates the
 * `vehicle` param against the org fleet (a never-owned reg returns an empty
 * result) and `includeExternal` is a permanent no-op kept for compat.
 *
 * @param {Object} [opts]
 * @param {string} [opts.vehicle] exact registration filter (org fleet only)
 * @param {string} [opts.dateFrom] inclusive YYYY-MM-DD (gap reappear-date >=)
 * @param {string} [opts.dateTo] inclusive YYYY-MM-DD (gap reappear-date <=)
 * @param {number} [opts.thresholdKm] minimum gap to report (TM uses 1 km)
 * @param {boolean} [opts.includeExternal] DEPRECATED no-op (never reveals
 *   other operators' vehicles)
 * @param {boolean} [opts.includeArchived] also include the org's ARCHIVED
 *   vehicles (default false = active fleet only)
 * @returns {{ events: Array<{vehicle, prev_driver, prev_close_odo,
 *             prev_close_time, start_driver, start_odo, start_time, diff_km}>,
 *             total_events: number, total_missing_km: number,
 *             threshold_km: number }}
 */
export async function fetchCardMileageGaps({
  vehicle, dateFrom, dateTo, thresholdKm, includeExternal = false,
  includeArchived = false,
} = {}) {
  const query = buildQuery({
    vehicle,
    date_from: dateFrom,
    date_to: dateTo,
    threshold_km: thresholdKm != null ? String(thresholdKm) : '',
    include_external: includeExternal ? 'true' : '',
    include_archived: includeArchived ? 'true' : '',
  });
  return getJson(`/card-mileage-gaps${query}`);
}

/**
 * GET /api/mileage/gap-breakdown?vehicle=&from=&to= — per-day + per-event
 * breakdown for ONE unaccounted-mileage gap window (compute-on-read; no
 * persistence). `from`/`to` are the gap's inclusive day window
 * (gap_start_day/gap_end_day off the /api/mileage row).
 * @param {{ vehicle: string, from: string, to: string }} params
 * @returns {Promise<{
 *   span: { from: string, to: string, days: number },
 *   per_day: Array<{ date: string, km: ?number,
 *     coverage: 'vu'|'vu_partial'|'not_uploaded',
 *     cause_signal: ?string, closure_state: string }>,
 *   events: Array<{ prev_driver: string, prev_time: string, prev_odo: ?number,
 *     next_driver: string, next_time: string, next_odo: ?number,
 *     km: ?number, tier: ?string }>,
 *   partial: boolean }>}
 */
export async function fetchGapBreakdown({ vehicle, from, to }) {
  const query = buildQuery({ vehicle, from, to });
  return getJson(`/mileage/gap-breakdown${query}`);
}

/**
 * GET /api/speeding
 */
export async function fetchSpeeding() {
  return getJson('/speeding');
}

/**
 * GET /api/timeline?driver=&date=
 */
export async function fetchTimeline(driver, date) {
  const query = buildQuery({ driver, date });
  return getJson(`/timeline${query}`);
}

/**
 * DELETE /api/data
 */
export async function deleteAllData() {
  const res = await request(`${BASE}/data`, { method: 'DELETE' });
  return res.json();
}

/**
 * GET /api/export/{table}
 */
export async function exportCsv(table) {
  const res = await request(`${BASE}/export/${encodeURIComponent(table)}`);
  const blob = await res.blob();

  const disposition = res.headers.get('Content-Disposition');
  let filename = `${table}.csv`;
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---- Admin API functions ---- */

export async function fetchUsers() {
  return getJson('/admin/users');
}

export async function createUser(data) {
  const res = await request(`${BASE}/admin/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function updateUser(userId, data) {
  const res = await request(`${BASE}/admin/users/${userId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deactivateUser(userId) {
  const res = await request(`${BASE}/admin/users/${userId}/deactivate`, {
    method: 'PUT',
  });
  return res.json();
}

export async function fetchOrganisations() {
  return getJson('/admin/organisations');
}

export async function createOrganisation(data) {
  const res = await request(`${BASE}/admin/organisations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function deleteOrganisation(orgId) {
  const res = await request(`${BASE}/admin/organisations/${orgId}`, {
    method: 'DELETE',
  });
  return res.json();
}

export async function addMembership(userId, data) {
  const res = await request(`${BASE}/admin/users/${userId}/memberships`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function removeMembership(userId, orgId) {
  const res = await request(`${BASE}/admin/users/${userId}/memberships/${orgId}`, {
    method: 'DELETE',
  });
  return res.json();
}

/**
 * GET /api/admin/analogue-disc-reads?month=YYYY-MM (super-admin only): per
 * organisation, the analogue discs first read in that UK calendar month,
 * the number Zed invoices, with where those discs are now and the free
 * reads (re-reads, failed or refused) beside them. A malformed month is a
 * 400 and anyone but a super-admin gets a 403, each thrown in the server's
 * own words.
 * @param {string} month 'YYYY-MM'
 * @returns {{ month, price_pence, rows: [...], totals: {...} }}
 */
export async function fetchAnalogueDiscReads(month) {
  return getJson(`/admin/analogue-disc-reads?month=${encodeURIComponent(month)}`);
}

/**
 * PUT /api/admin/analogue-engine-files (super-admin only): whether the
 * trace engine files an organisation's disc reads from now on (the Disc
 * reads tab's toggle, Zed 2026-10-15). 400 for a bad body, 403 for anyone
 * but a super-admin, 404 for an unknown organisation, each thrown in the
 * server's own words.
 * @param {string} organisationId
 * @param {boolean} enabled
 * @returns {Promise<{ organisation_id: string, engine_files: boolean }>}
 */
export async function setAnalogueEngineFiles(organisationId, enabled) {
  return putJson('/admin/analogue-engine-files', { organisation_id: organisationId, enabled });
}

/* ── The expert queue and the disc makes (design 2026-10-16 'any make',
   plan Task 6). Super-admin only: 403 for anyone else, each refusal
   thrown in the server's own words. ── */

/**
 * GET /api/admin/expert-queue: the trial discs waiting for the expert, each
 * with the stretches to decide.
 * @returns {Promise<{ discs: object[], counts: { discs: number, spots: number } }>}
 */
export async function fetchExpertQueue() {
  return getJson('/admin/expert-queue');
}

/**
 * POST /api/admin/expert-queue/{chartId}/answers: one disc's decisions, with
 * the photograph they were given on (409 when the disc has a new one since).
 * @returns {Promise<{ stored: number, applied: boolean }>}
 */
export async function postExpertAnswers(chartId, body) {
  return postJson(`/admin/expert-queue/${encodeURIComponent(String(chartId))}/answers`, body);
}

/** A queued disc's strip, as an object URL (the caller revokes it). */
export async function fetchExpertStrip(stripUrl) {
  const path = String(stripUrl).replace(/^\/api/, '');
  const res = await request(`${BASE}${path}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/**
 * GET /api/admin/analogue-makes: every disc make, its proof and its score on
 * the engine running now.
 * @returns {Promise<{ engine_version: number, makes: object[] }>}
 */
export async function fetchAnalogueMakes() {
  return getJson('/admin/analogue-makes');
}

/** POST .../{makeKey}/prove: switch the engine on for a ready make. */
export async function proveAnalogueMake(makeKey, engineVersion) {
  return postJson(`/admin/analogue-makes/${encodeURIComponent(String(makeKey))}/prove`,
    { engine_version: engineVersion });
}

/** POST .../{makeKey}/notes: the make's notes for the AI. */
export async function saveAnalogueMakeNotes(makeKey, notes) {
  return postJson(`/admin/analogue-makes/${encodeURIComponent(String(makeKey))}/notes`, { notes });
}

/**
 * POST /api/auth/free-trial — Create a free trial account with instant access.
 * No auth required.
 */
export async function fetchFreeTrial(data) {
  const res = await fetch(`${BASE}/auth/free-trial`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    let message = 'Failed to create trial account';
    try {
      const body = await res.json();
      if (body.detail) message = body.detail;
    } catch {
      // ignore parse errors
    }
    throw new Error(message);
  }
  return res.json();
}

export async function changePassword(currentPassword, newPassword) {
  const res = await request(`${BASE}/auth/change-password`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  });
  return res.json();
}

/* ---- Tachograph section APIs ---- */

export async function fetchCalendarSummary(start, end, driver, vehicle) {
  const query = buildQuery({ start, end, driver, vehicle });
  return getJson(`/calendar-summary${query}`);
}

/**
 * DEPRECATED — superseded by fetchRestAnalysis (/api/rest-analysis).
 * Kept for one release; no component should call this.
 */
export async function fetchRestSummary(start, end, driver) {
  const query = buildQuery({ start, end, driver });
  return getJson(`/rest-summary${query}`);
}

/**
 * GET /api/rest-analysis — engine-true Rest Exceptions + Weekly-Rest Ledger.
 * @returns {{ weekly_rests: [], duty_windows: [], exceptions: [] }}
 */
export async function fetchRestAnalysis(driver, start, end) {
  const query = buildQuery({ driver, start, end });
  return getJson(`/rest-analysis${query}`);
}

/**
 * GET /api/pattern/driver?driver=&months= — the driver's days as one wall
 * chart (design 2026-09-17-pattern-view §3, §5). `driver` is the calendar
 * key (the card-file identity the calendar's selectedDriver holds).
 * Server: { driver: { id, name, first }, from, to, tz, data_to, freshness,
 *           days: [ { date, segments: [[kind, start_min, end_min, vrn]],
 *                     totals, rest, findings: [{ id, code, severity_word,
 *                     headline, status }], wtd_min } ],   // newest first
 *           next: { sentence, band, state, drive_left_min, off_by, as_of } | null }
 * Resolves to null on any failure so the view can say "couldn't load"
 * rather than draw an empty chart that reads as clean.
 */
export async function fetchDriverPattern(driver, months = 2) {
  if (!driver) return null;
  try {
    return await getJson(`/pattern/driver${buildQuery({ driver, months })}`);
  } catch {
    return null;
  }
}

/**
 * GET /api/pattern/vehicle?vrn=&months= — the vehicle's days coloured by
 * who was driving (design 2026-09-17-pattern-view §4, §5).
 * Server: { vehicle: { vrn, plate }, from, to, tz,
 *           drivers: { d1: { id, name, first }, …, unknown: {…} },
 *           days: [ { date, downloaded: true|false|null, km, no_card_km,
 *                     segments: [[driverKey, start_min, end_min, kind]],
 *                     no_card_spans: [[start_min, end_min, km]] } ] }
 * null on any failure (same posture as fetchDriverPattern).
 */
export async function fetchVehiclePattern(vrn, months = 2) {
  if (!vrn) return null;
  try {
    return await getJson(`/pattern/vehicle${buildQuery({ vrn, months })}`);
  } catch {
    return null;
  }
}

/**
 * GET /api/vehicles
 * @param {boolean} [includeExternal] when true, return ALL vehicles
 *   (default owned-only). Each row carries operator_company + owned.
 * @param {boolean} [includeArchived] also the org's archived vehicles.
 * @param {boolean} [includeFormer] also declared vehicles handed to another
 *   operator (status 'former', handed_over_on 'YYYY-MM-DD').
 */
export async function fetchVehicles(includeExternal = false, includeArchived = false, includeFormer = false) {
  const query = buildQuery({
    include_external: includeExternal ? 'true' : '',
    include_archived: includeArchived ? 'true' : '',
    include_former: includeFormer ? 'true' : '',
  });
  return getJson(`/vehicles${query}`);
}

/**
 * GET /api/vehicles/lock-status
 * @param {boolean} [includeExternal] when true, return ALL vehicles
 *   (default owned-only). Each row carries operator_company + owned.
 */
export async function fetchVehicleLockStatus(includeExternal = false, includeArchived = false) {
  const query = buildQuery({
    include_external: includeExternal ? 'true' : '',
    include_archived: includeArchived ? 'true' : '',
  });
  return getJson(`/vehicles/lock-status${query}`);
}

export async function fetchFiles() {
  return getJson('/files');
}

export async function deleteFile(sourceFile) {
  const res = await request(`${BASE}/files/${encodeURIComponent(sourceFile)}`, {
    method: 'DELETE',
  });
  return res.json();
}

export async function closeInfringement(data) {
  const res = await request(`${BASE}/infringements/close`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

/**
 * POST /api/infringements/close-bulk — the durable bulk-close path. Writes a
 * real `infringement_closures` row per matched infringement (rebuild-proof;
 * survives re-analysis), unlike the status-only PATCH.
 *
 * Two modes, both durable:
 *  - explicit selection: { ids: [...], reason_code, comment }
 *  - unbounded historic disposition: { before_date, reason_code, comment } —
 *    closes EVERY still-open canonical infringement dated on/before
 *    before_date (used by the cockpit's "close all historic" action).
 *
 * @param {{ ids?: Array<string|number>, before_date?: string,
 *           reason_code: string, comment?: string }} data
 */
export async function closeBulkInfringements(data) {
  const res = await request(`${BASE}/infringements/close-bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function fetchInfringementClosures(dateFrom, dateTo) {
  const query = buildQuery({ date_from: dateFrom, date_to: dateTo });
  return getJson(`/infringement-closures${query}`);
}

export async function closeMileage(data) {
  const res = await request(`${BASE}/mileage/close`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function closeBulkMileage(data) {
  const res = await request(`${BASE}/mileage/close-bulk`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function fetchMileageClosures(dateFrom, dateTo) {
  const query = buildQuery({ date_from: dateFrom, date_to: dateTo });
  return getJson(`/mileage-closures${query}`);
}

/* ---- Mileage assignment APIs ---- */

export async function assignMileage(id, data) { return postJson(`/mileage/${id}/assign`, data); }
export async function bulkAssignMileage(data) { return postJson('/mileage/bulk-assign', data); }
export async function getMileageStats({ includeArchived = false } = {}) {
  const query = buildQuery({ include_archived: includeArchived ? 'true' : '' });
  return getJson(`/mileage-stats${query}`);
}

/* ---- Infringement lifecycle APIs ---- */

export async function updateInfringementStatus(id, data) {
  const res = await request(`${BASE}/infringements/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function getInfringementActions(id) {
  return getJson(`/infringements/${id}/actions`);
}

export async function bulkUpdateStatus(data) {
  const res = await request(`${BASE}/infringements/bulk-status`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function getInfringementStats() {
  return getJson('/infringement-stats');
}

/* ---- Org Settings / Drivers / Vehicles APIs ---- */

export async function getOrgSettings() { return getJson('/org/settings'); }
export async function updateOrgSettings(data) { return putJson('/org/settings', data); }
// Letterhead logo: a 'data:image/...;base64,...' URI. Empty string / clear
// removes it. Returns { logo_data } (the stored value).
export async function setOrgLogo(logoData) { return postJson('/org/settings/logo', { logo_data: logoData }); }
export async function clearOrgLogo() { return deleteJson('/org/settings/logo'); }
export async function getOrgDrivers(includeArchived = false) {
  const query = buildQuery({ include_archived: includeArchived ? 'true' : '' });
  return getJson(`/org/drivers${query}`);
}
export async function createOrgDriver(data) { return postJson('/org/drivers', data); }
export async function updateOrgDriver(id, data) { return putJson(`/org/drivers/${id}`, data); }
export async function deleteOrgDriver(id) { return deleteJson(`/org/drivers/${id}`); }
export async function getOrgVehicles(includeArchived = false) {
  const query = buildQuery({ include_archived: includeArchived ? 'true' : '' });
  return getJson(`/org/vehicles${query}`);
}
export async function createOrgVehicle(data) { return postJson('/org/vehicles', data); }
export async function updateOrgVehicle(id, data) { return putJson(`/org/vehicles/${id}`, data); }
export async function deleteOrgVehicle(id) { return deleteJson(`/org/vehicles/${id}`); }

/* ---- Card-renewal review APIs (#199) ---- */

/**
 * GET /api/renewal-candidates — PENDING card-renewal links for the caller's
 * org. A renewal = one driver got a new card; confirming links the two
 * driver records into one.
 * @returns {Array<{ id: string, reason: string,
 *   primary: { id: string, name: string, card_number: ?string },
 *   alias: { id: string, name: string, card_number: ?string },
 *   created_at: ?string }>}
 */
export async function getRenewalCandidates() { return getJson('/renewal-candidates'); }

/** POST /api/renewal-candidates/{id}/confirm — fold the alias card into the
 *  primary and rebuild the primary's unified canonical timeline. */
export async function confirmRenewal(id) { return postJson(`/renewal-candidates/${id}/confirm`); }

/** POST /api/renewal-candidates/{id}/reject — the two rows are NOT the same
 *  person; flip the link to 'rejected' (no merge, won't re-suggest). */
export async function rejectRenewal(id) { return postJson(`/renewal-candidates/${id}/reject`); }

/** POST /api/renewal-candidates/scan — auto-detect same-name renewals and
 *  queue them as PENDING links. @returns {{ queued: number }} */
export async function scanRenewals() { return postJson('/renewal-candidates/scan'); }

/* ---- Speed data & harsh braking APIs ---- */

export async function fetchSpeedData(vehicle, date) {
  const query = buildQuery({ vehicle, date });
  return getJson(`/speed-data${query}`);
}

/**
 * GET /api/harsh-braking — canonical harsh-braking events for the org.
 * @param {object} [params] vehicle / driver / date_from / date_to filters.
 * @param {number} [params.minDeceleration] raise the read-time severity floor
 *   (m/s²). Mapped to the `min_deceleration` query param; the server clamps it
 *   to the stored capture floor (7) and never below.
 */
export async function fetchHarshBraking(params = {}) {
  const { minDeceleration, ...rest } = params;
  const query = buildQuery({
    ...rest,
    min_deceleration: minDeceleration,
  });
  return getJson(`/harsh-braking${query}`);
}

/**
 * GET /api/speed-policy — the org's persisted harsh-braking calibration.
 * @returns {Promise<{ harsh_braking_min_decel: number }>}
 */
export async function getSpeedPolicy() {
  return getJson('/speed-policy');
}

/**
 * PUT /api/speed-policy — persist the org's harsh-braking calibration threshold.
 * @param {{ harshBrakingMinDecel: number }} policy
 * @returns {Promise<{ harsh_braking_min_decel: number }>} the clamped value.
 */
export async function setSpeedPolicy({ harshBrakingMinDecel }) {
  return putJson('/speed-policy', {
    harsh_braking_min_decel: harshBrakingMinDecel,
  });
}

/**
 * GET /api/vehicle-data-status
 * @param {boolean} [includeExternal] when true, return ALL vehicles
 *   (default owned-only). Each row carries operator_company + owned.
 */
export async function fetchVehicleDataStatus(includeExternal = false, includeArchived = false) {
  const query = buildQuery({
    include_external: includeExternal ? 'true' : '',
    include_archived: includeArchived ? 'true' : '',
  });
  return getJson(`/vehicle-data-status${query}`);
}

export async function fetchCardEvents(driver, date) {
  const query = buildQuery({ driver, date });
  return getJson(`/card-events${query}`);
}

/**
 * GET /api/walkarounds?driver=&date= — the DDIR driver-app vehicle checks
 * attributed to this driver on this day, for the Activity Log pill.
 *
 * Filed by the check's completed_at, so a check that synced days late still
 * appears on the day it was actually done.
 *
 * Expect [] most of the time: DDIR's sender is not built yet, and a driver
 * whose card/DDIR account is not linked yet has their checks resolve to
 * nobody (deliberately — TP never name-matches a walkaround). Both are
 * "nothing to show", not errors, so the caller renders no pills.
 *
 * @returns {Promise<Array<{check_id: string, vehicle_registration: string,
 *   completed_at: string, result: 'pass'|'fail', defect_count: number,
 *   defects: Array<{item?: string, severity?: string, note?: string}>,
 *   driver_matched: boolean}>>}
 */
export async function fetchWalkaroundsForDay(driver, date) {
  const query = buildQuery({ driver, date });
  return getJson(`/walkarounds${query}`);
}

/**
 * GET /api/card-out-assumed — the times this driver took the card out with
 * the mode still on Work or Availability, and the loader treated the time
 * out as rest because it assumed the shift had ended (Zed, 2026-10-22).
 * Every span that starts in or overlaps date_from..date_to, any status.
 * Times are stored UTC 'YYYY-MM-DD HH:MM:SS', like the activity rows.
 *
 * The Deep Dive asks for the day before as well, so a span that began the
 * evening before and ran past midnight is caught. A 4xx/5xx throws: the
 * caller says nothing rather than claim there were none.
 *
 * @returns {Promise<{spans: Array<{start: string, end: string,
 *   minutes: number, recorded: 'Work'|'Availability', vehicle: string}>}>}
 */
export async function fetchCardOutAssumed(driver, dateFrom, dateTo) {
  return getJson(
    `/card-out-assumed?driver=${encodeURIComponent(driver ?? '')}`
    + `&date_from=${encodeURIComponent(dateFrom ?? '')}`
    + `&date_to=${encodeURIComponent(dateTo ?? '')}`,
  );
}

export async function fetchSpeedingForDay(driver, date) {
  const query = buildQuery({ driver, date });
  return getJson(`/speeding${query}`);
}

export async function fetchHarshBrakingForDay(driver, date) {
  const query = buildQuery({ driver, date_from: date, date_to: date });
  return getJson(`/harsh-braking${query}`);
}

export async function updateHarshBrakingStatus(id, data) {
  const res = await request(`${BASE}/harsh-braking/${id}/status`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function extractSpeedData(sourceFile) {
  return postJson('/speed-data/extract', { source_file: sourceFile });
}

/* ---- CERT-2: speed-trace evidence APIs ---- */

/**
 * GET /api/speed-coverage — which OWNED vehicles have detailed second-by-second
 * speed data behind them, and which have NONE. A vehicle with no detailed-speed
 * block shows "0 harsh-braking" only because there is nothing to detect on —
 * that is "no data", not "clean".
 * @returns {{ covered: Array<{registration: string, file_count: number}>,
 *             no_speed_data: Array<{registration: string}> }}
 */
export async function getSpeedCoverage() {
  return getJson('/speed-coverage');
}

/**
 * GET /api/speed-events/{kind}/{id}/trace — the raw second-by-second speed
 * trace sliced server-side to ±window seconds around ONE flagged event, so a
 * TM can SEE the spike / deceleration.
 * @param {'speeding'|'harsh'} kind
 * @param {string|number} id event id
 * @param {number} [window=60] half-window in seconds around the event
 * @returns {{ vehicle: string, event_time: string, window: number,
 *             points: Array<{t: string, speed_km_h: number}>,
 *             marker: {event_time: string, deceleration?: number,
 *                      speed?: number, limit?: number} }}
 */
export async function getSpeedEventTrace(kind, id, window = 60) {
  return getJson(`/speed-events/${kind}/${encodeURIComponent(id)}/trace?window=${window}`);
}

/* ---- Driver Communication APIs ---- */

export async function sendToDriver(data) { return postJson('/infringements/send-to-driver', data); }
/**
 * GET /api/infringement-letter/{id}[?paper=1] — the stored note as HTML.
 * `paper` adds the print footer ("Printed by … for signature on paper")
 * and the signature block (design 2026-09-12 signed-copies-phase4 §3);
 * without it the render is unchanged.
 */
export async function getInfringementLetter(id, { paper = false } = {}) {
  const res = await request(`${BASE}/infringement-letter/${id}${paper ? '?paper=1' : ''}`);
  return res.text();
}
export async function previewInfringementLetter(data) { return postJson('/infringement-letter/preview', data); }
export async function getCommunications(infringementId) { return getJson(`/communications/${infringementId}`); }

/* ---- Sign-off P1 — Andre review / escalation decision APIs ---- */

/**
 * GET /api/infringements/{id}/recommendation — Andre's escalation review for
 * one infringement: the infringement detail, cached plain-English explanation,
 * seriousness banding, potential graduated fixed penalty, the driver's
 * 12-week history, and the recommended escalation tier.
 * @param {string|number} id infringement id
 * @returns {Promise<{
 *   infringement: { id, date, type, code, rule },
 *   explanation: ?string,
 *   seriousness: { code, label, gloss },
 *   potential_penalty: { band, gbp: ?number, note },
 *   history: { count: number, recent: Array<{date, category, tier, stage?}> },
 *   recommendation: { tier, reason, send_to_app: boolean,
 *                     stage, stage_word, reasons: string[],
 *                     position: { stage, stage_word, lives_until } | null } }>}
 * The stage keys are the ladder (design 2026-09-11 ladder-phase3 §5); a
 * server without them still answers in tiers and lib/ladder derives a stage.
 */
export async function getInfringementRecommendation(id) {
  return getJson(`/infringements/${id}/recommendation`);
}

/**
 * POST /api/infringements/{id}/escalation-decision — record the manager's
 * chosen stage (design 2026-09-11 ladder-phase3 §4: a stage above `note`
 * becomes a warning with a life). `tier` is the stage's escalation tier,
 * sent alongside so a server that still reads tiers records the same
 * decision. Overriding the recommended stage requires a written
 * override_reason; the server returns 400 without it (the UI gates this too).
 * The body may carry `training: { kind, subject, module_id?, due_by?, send }`
 * so the stage and the training are recorded in one go (design 2026-09-11
 * training-phase3b §5); the response then gains `training_id`,
 * `training_sent` and `training_reason` (null | 'no_ddir_user' |
 * 'org_unmapped' | 'ddir_unavailable').
 * @param {string|number} id infringement id
 * @param {{ stage: string, tier: string, override_reason?: string, manager_comments?: string,
 *           training?: { kind: string, subject: string, module_id?: string, due_by?: string, send: boolean } }} body
 * @returns {Promise<{ tier, stage?, overridden: boolean, recommended_tier,
 *                     training_id?: number|null, training_sent?: boolean, training_reason?: string|null }>}
 */
export async function postEscalationDecision(id, body) {
  return postJson(`/infringements/${id}/escalation-decision`, body);
}

/**
 * GET /api/infringements/{id}/closure-document — the single auditable closure
 * artifact (org header, infringement detail, explanation, seriousness +
 * penalty, the manager's recorded decision, the action timeline, and the
 * dual-signature block) as printable HTML.
 * `paper` adds the print footer and signature block (design 2026-09-12
 * signed-copies-phase4 §3); without it the render is unchanged.
 * @param {string|number} id infringement id
 * @returns {Promise<{ html: string }>}
 */
export async function getClosureDocument(id, { paper = false } = {}) {
  return getJson(`/infringements/${id}/closure-document${paper ? '?paper=1' : ''}`);
}

/**
 * GET /api/infringements/{id}/case — the consolidated sign-off case bundle
 * for one infringement (detail, explanation, seriousness/penalty, history,
 * recommendation, action timeline, manager comments).
 * @param {string|number} id infringement id
 */
export async function getInfringementCase(id) {
  return getJson(`/infringements/${id}/case`);
}

/**
 * POST /api/infringements/{id}/manager-comments — persist the manager's
 * free-text comments on the case.
 * @param {string|number} id infringement id
 * @param {string} comments
 */
export async function saveManagerComments(id, comments) {
  return postJson(`/infringements/${id}/manager-comments`, { comments });
}

/**
 * POST /api/infringements/{id}/sign-and-close — record the dual-signature
 * decision and close the case.
 * @param {string|number} id infringement id
 * @param {{ decision: string, decision_reason?: string }} data
 */
export async function signAndClose(id, data) {
  return postJson(`/infringements/${id}/sign-and-close`, data);
}

/* ---- Deep Dive / Day detail APIs ---- */

export async function fetchDayActivities(driver, date) {
  return getJson(`/activities?driver=${encodeURIComponent(driver)}&date_from=${date}&date_to=${date}&limit=50000`);
}

export async function fetchDayInfringements(driver, date) {
  return getJson(`/infringements?driver=${encodeURIComponent(driver)}&date_from=${date}&date_to=${date}`);
}

export async function fetchVehicleActivities(vehicle, date) {
  return getJson(`/activities?vehicle=${encodeURIComponent(vehicle)}&date_from=${date}&date_to=${date}&limit=50000`);
}

/**
 * GET /api/activities?vehicle=&source=vu — the truck's OWN record for a day:
 * the rows of the newest vehicle-unit download covering it, every driver it
 * recorded (2026-10-22). fetchVehicleActivities reads the driver-merged
 * canonical rows, which on a one-driver day are the driver card again.
 * Each row carries vu_download and vu_uploaded_at.
 */
export async function fetchVehicleUnitDay(vehicle, date) {
  return getJson(`/activities?vehicle=${encodeURIComponent(vehicle)}&date_from=${date}&date_to=${date}&source=vu&limit=50000`);
}

/**
 * GET /api/vehicle-card-events?vehicle=&date= — the vehicle-scoped CARD IN /
 * CARD OUT feed for one owned vehicle on one day (VU card-IW records — same
 * source as the missing-mileage gap detector, so the insertion/withdrawal
 * odometers match). Powers the vehicle-mode day-view journey narrative.
 * @param {string} vehicle registration (owned; never-owned regs return [])
 * @param {string} date YYYY-MM-DD
 * @returns {Promise<Array<{type:'in'|'out', time: string,
 *   odometer: ?number, driver_name: string}>>} ordered by time.
 */
export async function fetchVehicleCardEvents(vehicle, date) {
  const query = buildQuery({ vehicle, date });
  return getJson(`/vehicle-card-events${query}`);
}

/**
 * GET /api/vehicle-vu-coverage?vehicle=&date= — whether any VU download's
 * coverage window spans this vehicle-day (same file_metadata windows as the
 * calendar's vehiclesWithoutVu; shared helper server-side). Powers the
 * Vehicle-Mode day view's "card data only" qualification: source is NOT
 * derivable from the activity rows themselves (canonical rows lose their
 * card/VU origin under 'merged::<driver>'), so the day view asks here.
 * @param {string} vehicle registration
 * @param {string} date YYYY-MM-DD
 * @returns {Promise<{vehicle: string, date: string,
 *   vuCovered: (boolean|null)}>} vuCovered null = lookup failed server-side
 *   (caller must degrade to the unqualified rendering).
 */
export async function fetchVehicleVuCoverage(vehicle, date) {
  const query = buildQuery({ vehicle, date });
  return getJson(`/vehicle-vu-coverage${query}`);
}

export async function fetchRawData(sourceFile) {
  return getJson(`/raw-data/${encodeURIComponent(sourceFile)}`);
}

/* ---- Deep file viewer (Tech Mode / TachoMaster-style) ---- */

/**
 * GET /api/viewer/files — org's file-picker list.
 * @returns {Array<{ source_file, file_type, label, upload_timestamp }>}
 *   One entry per underlying uploaded file (per-driver/canonical splits
 *   already collapsed/excluded server-side).
 */
export async function listViewerFiles() {
  return getJson('/viewer/files');
}

/**
 * GET /api/file-detail/{source_file} — full parsed detail for one file.
 * @param {string} sourceFile
 * @returns {{ header: object, sections: Object<string, (Array|object)> }}
 *   header carries file_type, generation, signature_verified, etc.;
 *   sections is a dict of the full untruncated parsed blocks under their
 *   native keys.
 */
export async function getFileDetail(sourceFile) {
  return getJson(`/file-detail/${encodeURIComponent(sourceFile)}`);
}

/* ---- Report generation API ---- */

export async function generateReport(reportType, params = {}) {
  const query = buildQuery(params);
  return getJson(`/reports/${encodeURIComponent(reportType)}${query}`);
}

/* ---- Saved Views (RPT-P2): named report family + controls, org-scoped ---- */

export async function fetchSavedViews() {
  return getJson('/saved-views');
}

export async function createSavedView({ name, report_family, params }) {
  return postJson('/saved-views', { name, report_family, params });
}

export async function deleteSavedView(id) {
  return deleteJson(`/saved-views/${encodeURIComponent(id)}`);
}

/* ---- Report schedules (RPT-P3): auto-emailed report deliveries ---- */

export async function fetchReportSchedules() {
  return getJson('/report-schedules');
}

export async function createReportSchedule(payload) {
  return postJson('/report-schedules', payload);
}

export async function deleteReportSchedule(id) {
  return deleteJson(`/report-schedules/${encodeURIComponent(id)}`);
}

/**
 * GET /api/reports/earned-recognition?period_end=&periods=
 * Live DVSA Earned Recognition D1–D9 scorecard, newest period first.
 * @param {Object} [params]
 * @param {string} [params.period_end] YYYY-MM-DD end of the newest
 *   28-day period (defaults to today server-side).
 * @param {number} [params.periods] number of consecutive periods (1–13).
 * @returns {{ periods: Array<{period_start, period_end, iso_weeks,
 *   tachograph_days, band_counts, wtd_count, overall_count,
 *   repeat_offenders, msi_count, unaccounted_mileage,
 *   kpis: Object<string,{value:number,threshold:number}>,
 *   triggers: Object<string,'GREEN'|'YELLOW'|'AMBER'|'RED'>,
 *   contributing: Object<string,number[]>}> }}
 */
export async function fetchEarnedRecognition(params = {}) {
  const query = buildQuery(params);
  return getJson(`/reports/earned-recognition${query}`);
}

/* ---- File download APIs ---- */

export async function downloadFile(sourceFile) {
  const res = await request(`${BASE}/files/${encodeURIComponent(sourceFile)}/download`);
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  let filename = sourceFile;
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function downloadFilesZip(files) {
  const res = await request(`${BASE}/files/download-zip`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ files }),
  });
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  let filename = 'tachoparser_export.zip';
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---- CERT-3: Data Integrity APIs ---- */

/**
 * GET /api/data-integrity — run all 4 integrity checks for the caller's org.
 * @returns {{ summary: { unexplained: number, signed: number, by_check: Object },
 *             unexplained: Array<{check, key, severity, detail}>,
 *             signed: Array<{check, key, severity, detail}> }}
 */
export async function getDataIntegrity() {
  return getJson('/data-integrity');
}

/* ---- Fraud detection APIs ---- */

export async function fetchFraudAlerts(params = {}) {
  const query = buildQuery(params);
  return getJson(`/fraud-alerts${query}`);
}

export async function updateFraudAlert(alertId, data) {
  const res = await request(`${BASE}/fraud-alerts/${alertId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  return res.json();
}

export async function triggerFraudScan() {
  return postJson('/fraud-scan');
}

/* ---- Card-left-on-Work review (the manager's correction loop) ---- */

/**
 * GET /api/card-left-on-work/{alert_id} — what this alert is holding.
 *
 * A card-left-on-Work alert withholds a chain of consequent infringements
 * (insufficient daily rest, 6h-no-break, WTD night work) that are NOT real
 * drivers'-hours breaches — the fault is a mode-switch error at end of shift.
 * This is the only place that joins the alert to those infringements, and
 * `held_count` is the number that drives the manager's decision.
 *
 * `proposed_finish_basis` has THREE values and they are not interchangeable:
 *   - `derived`     — a value computed from a real activity row. The panel
 *                     shows it as "Work mode began at HH:MM" (never as a
 *                     "proposed finish" — see cliReview.js for why).
 *   - `no_evidence` — the timeline was read and holds nothing to derive from.
 *   - `unavailable` — the read FAILED. The evidence may exist; we could not see
 *                     it. The panel offers a retry, NOT manual entry.
 *
 * Read-only: writes nothing, re-derives nothing.
 *
 * @param {string} alertId fraud_alerts.id (org-scoped server-side; another
 *   tenant's alert is a 404)
 * @returns {Promise<{alert_id: string, status: string, driver_name: string,
 *   vehicle_registration: string, span_start: string, span_end: string,
 *   span_hours: number|null, proposed_finish: string|null,
 *   proposed_finish_basis: 'derived'|'no_evidence'|'unavailable',
 *   held_infringements: Array<{id: number, date: string, type: string}>,
 *   held_count: number}>}
 */
export async function fetchCardLeftOnWorkReview(alertId) {
  return getJson(`/card-left-on-work/${encodeURIComponent(alertId)}`);
}

/**
 * POST /api/card-left-on-work/{alert_id}/resolve — record the correction and
 * close what the alert is holding.
 *
 * Writes an evidenced `hours_annotations` row (carrying BOTH the derived value
 * and the manager's entry, so any departure is itself on record) and closes the
 * held infringements through the existing bulk-closure machinery with reason
 * `driver_error_mode_switch`. The raw canonical timeline is never touched — a
 * DVSA examiner recomputing from the raw .ddd files gets the same answer we do,
 * and every difference is visible as an annotation rather than an absence.
 *
 * Refusals the caller must be ready to show verbatim (`Error.message` carries
 * the server's `detail`, which is written for a manager to act on):
 *   - 422 unknown/absent instruction code, or the driver cannot be resolved
 *   - 400 corrected_finish absent, unreadable, or outside the recorded span
 *   - 409 a live annotation already exists — someone else resolved it first
 *
 * @param {string} alertId
 * @param {{corrected_finish: string, instruction_code: string,
 *          reason?: string}} body
 * @returns {Promise<{annotation_id: string, closed_count: number,
 *   instruction: {code: string, text: string}, driver_name: string,
 *   proposed_finish: string|null, corrected_finish: string,
 *   reason_code: string}>}
 */
export async function resolveCardLeftOnWork(alertId, body) {
  return postJson(`/card-left-on-work/${encodeURIComponent(alertId)}/resolve`, body);
}

/* ---- DDIR integration APIs (A12) ---- */

/**
 * GET /api/integrations/ddir/quarantine — operator-facing queue of
 * tacho uploads waiting on a DDIR-side driver/vehicle add. The
 * embedded React page polls this every 30s.
 */
export async function fetchDdirQuarantine() {
  return getJson('/integrations/ddir/quarantine');
}

/**
 * POST /api/quarantine/recheck (T2) — re-run the quarantine gate for every
 * pending row of the org against the CURRENT drivers/vehicles roster.
 * Rows that now pass flip to 'ready_reupload' (re-upload them — the gate
 * will accept).
 * @returns {{checked: number,
 *            released: Array<{id: string, upload_id: string, file_type: string}>,
 *            still_blocked: Array<{id: string, status: string}>}}
 */
export async function recheckQuarantine() {
  return postJson('/quarantine/recheck');
}

/**
 * GET /api/quarantine/summary (T3) — light dashboard counts for the
 * quarantine alert chip.
 * @returns {{pending: number, ready_reupload: number}}
 */
export async function fetchQuarantineSummary() {
  return getJson('/quarantine/summary');
}

/**
 * DELETE /api/quarantine/{id} — soft-delete a quarantine row the operator
 * has decided is noise. The row is removed from the active queue but kept
 * in audit history (status flips to 'deleted'; bytes were never stored).
 * Org-scoped server-side; a row in another org returns 404.
 * @param {string} id quarantine row id
 * @returns {{ success: boolean, id: string }}
 */
export async function deleteQuarantineFile(id) {
  return deleteJson(`/quarantine/${encodeURIComponent(id)}`);
}

/* ---- helpers ---- */

function buildQuery(params) {
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && v !== '',
  );
  if (entries.length === 0) return '';
  const qs = new URLSearchParams(entries).toString();
  return `?${qs}`;
}

/**
 * GET /api/driver-status-grid — per-driver rollup for the dashboard's
 * Driver Status Grid (rendered beneath the Vehicle Status Grid): card
 * freshness (ok/overdue/never_downloaded/in_quarantine), coverage end
 * (how far the card DATA runs) + last upload, and 12-month infringement
 * aggregates (NEW/open/closed/most-common/28d rate).
 * @param {boolean} [includeArchived] when true, also return archived drivers
 *   (default active-only). archived_count is always org-wide.
 * @returns {{generated_at: string, archived_count: number,
 *            driver_count: number, drivers: Array<object>}}
 */
export async function getDriverStatusGrid(includeArchived = false) {
  const query = buildQuery({
    include_archived: includeArchived ? 'true' : '',
  });
  return getJson(`/driver-status-grid${query}`);
}

/* ---- Raw .ddd export (DVSA) — Data Centre ---- */

function buildRawExportParams({ dateFrom, dateTo, dateMode, driverNames, vrns, allDrivers, allVehicles }) {
  const params = new URLSearchParams();
  params.set('date_from', dateFrom);
  params.set('date_to', dateTo);
  params.set('date_mode', dateMode || 'upload');
  (driverNames || []).forEach((n) => params.append('driver_names', n));
  (vrns || []).forEach((v) => params.append('vrns', v));
  if (allDrivers) params.set('all_drivers', 'true');
  if (allVehicles) params.set('all_vehicles', 'true');
  return params;
}

/**
 * GET /api/files/export-raw/preview — admin-only dry-run of a raw .ddd
 * export: what the current selection would put in the zip.
 * @returns {{count: number, total_known: number, missing_raw_count: number,
 *            max_files: number, files: Array<{source_file, file_type,
 *            driver_name, upload_timestamp, has_raw}>}}
 */
export async function previewRawExport(opts) {
  const params = buildRawExportParams(opts);
  return getJson(`/files/export-raw/preview?${params.toString()}`);
}

/**
 * POST /api/files/export-raw — download the retained raw .ddd files for a
 * period as a zip (DVSA request). Saves the blob like downloadFilesZip.
 * Files matched in the ledger but missing raw bytes (pre-retention history)
 * are listed in the zip's manifest.txt as missing_raw.
 */
export async function downloadRawExport({ dateFrom, dateTo, dateMode, driverNames, vrns, allDrivers, allVehicles }) {
  const selector = {};
  if (driverNames && driverNames.length) selector.driver_names = driverNames;
  if (vrns && vrns.length) selector.vrns = vrns;
  if (allDrivers) selector.all_drivers = true;
  if (allVehicles) selector.all_vehicles = true;
  const res = await request(`${BASE}/files/export-raw`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      selector,
      date_from: dateFrom,
      date_to: dateTo,
      date_mode: dateMode || 'upload',
    }),
  });
  const blob = await res.blob();
  const disposition = res.headers.get('Content-Disposition');
  let filename = 'tachoparser_raw_export.zip';
  if (disposition) {
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match) filename = match[1];
  }
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ---- TM Cockpit (Overview redesign) APIs ---- */
/*
 * Each helper mirrors the getJson pattern but NEVER throws to the caller on a
 * non-200 (403 not-enrolled / 404 / 500 / network): the cockpit bands must
 * render even when one endpoint is unavailable, so we fail soft to an empty
 * default whose shape matches the server response exactly. (A genuine 401 auth
 * expiry still triggers request()'s reload before the catch swallows it.)
 * Shapes verified against api/server.py + api/auth_routes.py.
 */

/**
 * GET /api/infringements/action-queue — the "needs-you-now" SLA queue.
 * Server: { rows: [...], counts: {unsent_send_overdue, unsent_send_open, sig_overdue, sig_open,
 *           held_review} } — held_review: WR-COMP held for manager review, no SLA.
 */
export async function fetchActionQueue() {
  try {
    return await getJson('/infringements/action-queue');
  } catch {
    return {
      rows: [],
      counts: {
        unsent_send_overdue: 0,
        unsent_send_open: 0,
        sig_overdue: 0,
        sig_open: 0,
        held_review: 0,
      },
      // Additive: lets the cockpit tell "the queue is empty" apart from
      // "the queue never loaded" — a failed fetch must never render as
      // a clear fleet.
      unavailable: true,
    };
  }
}

/**
 * GET /api/behaviour-trends — 28d-vs-previous speeding / harsh-braking trend.
 * Server: { recent_28d:{speeding,harsh_braking}, prev_28d:{...}, change_pct:{speeding,harsh_braking} }
 */
export async function fetchBehaviourTrends() {
  try {
    return await getJson('/behaviour-trends');
  } catch {
    return {
      recent_28d: { speeding: 0, harsh_braking: 0 },
      prev_28d: { speeding: 0, harsh_braking: 0 },
      change_pct: { speeding: 0, harsh_braking: 0 },
    };
  }
}

/**
 * GET /api/health-strip — the five-block system health bar (v3.48.0).
 * One compute-on-read payload: the five block faces AND their drill-modal
 * detail rows together. Server shape:
 *   { standards:{veh_red,veh_amber,card_red,card_amber,auto},
 *     vehicle_data:{red,amber,green,vehicles:[...]},
 *     driver_data:{red,amber,green,drivers:[...]},
 *     unaccounted:{open_km,vehicles_n,vehicles:[...]},
 *     infringements:{open,drivers_n,drivers:[...]},
 *     behaviour:{speeding_28d,hb_28d,speeding_trend_pct,hb_trend_pct,
 *                speeding_drivers:[...],hb_vehicles:[...]} }
 * Fail-soft: a shape-matched empty payload on error so the strip renders
 * quiet zeros rather than crashing the dashboard shell.
 */
export async function fetchHealthStrip() {
  try {
    return await getJson('/health-strip');
  } catch {
    return {
      standards: { veh_red: 30, veh_amber: 21, card_red: 14, card_amber: 7, auto: false },
      vehicle_data: { red: 0, amber: 0, green: 0, vehicles: [] },
      driver_data: { red: 0, amber: 0, green: 0, drivers: [] },
      unaccounted: { open_km: 0, vehicles_n: 0, vehicles: [] },
      infringements: { open: 0, drivers_n: 0, drivers: [] },
      behaviour: {
        speeding_28d: 0,
        hb_28d: 0,
        speeding_trend_pct: 0,
        hb_trend_pct: 0,
        speeding_drivers: [],
        hb_vehicles: [],
      },
    };
  }
}

/**
 * GET /api/infringements/top-types — fleet-wide most-common infringement roll-up.
 * Server: { rows: [...], date_from }
 */
export async function fetchFleetTopTypes() {
  try {
    return await getJson('/infringements/top-types');
  } catch {
    return { rows: [], date_from: null };
  }
}

/**
 * GET /api/today — the Today tab's one read (design 2026-09-08 §2).
 * Server: { as_of:{card_data_to,truck_data_to,days_old,band,auto_download},
 *           week:{from,to,drivers_out,drivers_clear,drivers_not_measured,not_clear:[...]},
 *           yesterday:{date,drivers_out,drivers_clear},
 *           streaks:[...], worth_a_word:{...}|null,
 *           week_high_five:{eligible,week_to,clean,out,can_send,sent_at,sent_week_to}|null }
 * Any section the server could not compute arrives as null. A failed fetch
 * resolves to null (not a shaped zero) so the screen says "couldn't load"
 * rather than rendering an empty yard as a clean one.
 */
export async function fetchToday() {
  try {
    return await getJson('/today');
  } catch {
    return null;
  }
}

/**
 * GET /api/tomorrow — one "what can they do next" line per driver,
 * projected from the data end and stamped with that driver's card
 * freshness (design 2026-09-08-tomorrow §5).
 * Server: { as_of, drivers: [ { driver_id, name, first_name, file_identity,
 *           band, card_to, state, line, projection } ] | null }
 * The server writes `line`; the client renders it verbatim.
 * Resolves to null on any failure so the card can say "couldn't load".
 */
export async function fetchTomorrow() {
  try {
    return await getJson('/tomorrow');
  } catch {
    return null;
  }
}

/**
 * GET /api/drivers/{driver_id}/score — one driver's score with its working
 * (design 2026-09-11 driver-score-phase2 §2, §3).
 * Server: { balance, live_points, credits, clean_weeks,
 *           events: [ { date, kind, severity, points, live, headline? } ],
 *           window_weeks, fade_to_zero, repeat_offender,
 *           periods: [ { from, to, findings } ], findings_12w, last_finding,
 *           policy_version: number | 'preset',
 *           scale: { minor, serious, very_serious, most_serious } }
 * 404 for a driver outside the org. Resolves to null on any failure so the
 * card can say "couldn't load" rather than show a 0 that reads as clean.
 */
export async function fetchDriverScore(driverId) {
  if (driverId === null || driverId === undefined || driverId === '') return null;
  try {
    return await getJson(`/drivers/${encodeURIComponent(String(driverId))}/score`);
  } catch {
    return null;
  }
}

/**
 * GET /api/drivers/{driver_id}/warnings — the driver's warnings ledger and
 * position on the ladder (design 2026-09-11 ladder-phase3 §4).
 * Server: { warnings: [ { id, stage, stage_word, issued_at, issued_by,
 *                         lives_until, status: 'live'|'lapsed'|'withdrawn',
 *                         outcome, outcome_at, source_infringement_id, notes } ],
 *           position: { stage, stage_word, lives_until } | null,
 *           policy_version }
 * 404 for a driver outside the org. Resolves to null on any failure so the
 * card can say "couldn't load" rather than read as a clean record.
 */
export async function fetchDriverWarnings(driverId) {
  if (driverId === null || driverId === undefined || driverId === '') return null;
  try {
    return await getJson(`/drivers/${encodeURIComponent(String(driverId))}/warnings`);
  } catch {
    return null;
  }
}

/**
 * POST /api/drivers/{driver_id}/warnings/{warning_id}/withdraw — an admin
 * withdraws a warning issued in error, with a written reason (design §4;
 * audit row on the server). Unlike the reads this throws on failure: the
 * card shows the server's message beside the reason box.
 * @param {string|number} driverId
 * @param {string|number} warningId
 * @param {string} reason — required; an empty reason is refused here
 */
export async function withdrawWarning(driverId, warningId, reason) {
  const why = typeof reason === 'string' ? reason.trim() : '';
  if (!why) throw new Error('A reason is required to withdraw a warning');
  return postJson(
    `/drivers/${encodeURIComponent(String(driverId))}/warnings/${encodeURIComponent(String(warningId))}/withdraw`,
    { reason: why },
  );
}

/* ── Training beside the stage (design 2026-09-11 training-phase3b §5) ── */

/**
 * GET /api/org/training-library — the driver app's training library for
 * the review wizard's subject and module selects.
 * Server (always 200, fail-soft): { subjects: [{ group, subjects: [label] }],
 *   modules: [{ module_id, title, section, subjects: [label], version, url }],
 *   available: bool, reason: null | 'org_unmapped' | 'ddir_unavailable' }
 * Resolves to null on any failure so the wizard falls back to a text box.
 */
export async function fetchTrainingLibrary() {
  try {
    return await getJson('/org/training-library');
  } catch {
    return null;
  }
}

/**
 * GET /api/drivers/{driver_id}/training — the driver's training record.
 * Server: { training: [ { id, kind, kind_word, subject, module_title,
 *                         assigned_at, assigned_by, due_by, completed_at,
 *                         completed_by, bulletin_id, notes,
 *                         status: 'assigned'|'completed'|'cancelled',
 *                         source_infringement_id } ],
 *           open: n, policy_version }
 * 404 for a driver outside the org. Resolves to null on any failure so the
 * card can say "couldn't load" rather than read as no training on record.
 */
export async function fetchDriverTraining(driverId) {
  if (driverId === null || driverId === undefined || driverId === '') return null;
  try {
    return await getJson(`/drivers/${encodeURIComponent(String(driverId))}/training`);
  } catch {
    return null;
  }
}

/**
 * POST /api/drivers/{driver_id}/training — an admin records training on
 * its own (outside a decision). `{ kind, subject, module_id?, due_by?,
 * notes?, send }` → `{ training_id, sent, reason }`; a library training
 * the app could not deliver is still recorded, with `sent: false` and the
 * reason. Throws on failure, as the writes do.
 */
export async function assignTraining(driverId, body) {
  return postJson(`/drivers/${encodeURIComponent(String(driverId))}/training`, body || {});
}

/**
 * POST /api/drivers/{driver_id}/training/{id}/complete — an admin marks a
 * training done (`{ completed_at?, notes? }`; today when no date is given).
 * Throws on failure: the card shows the server's words beside the row.
 */
export async function completeTraining(driverId, trainingId, body) {
  return postJson(
    `/drivers/${encodeURIComponent(String(driverId))}/training/${encodeURIComponent(String(trainingId))}/complete`,
    body || {},
  );
}

/**
 * POST /api/drivers/{driver_id}/training/{id}/cancel — an admin cancels an
 * assigned training, with a written reason. An empty reason is refused
 * here; the server's message is thrown on failure.
 */
export async function cancelTraining(driverId, trainingId, reason) {
  const why = typeof reason === 'string' ? reason.trim() : '';
  if (!why) throw new Error('A reason is required to cancel training');
  return postJson(
    `/drivers/${encodeURIComponent(String(driverId))}/training/${encodeURIComponent(String(trainingId))}/cancel`,
    { reason: why },
  );
}

/**
 * The Today card's sends (thanks, high five) go to the driver app through
 * the server. Unlike the reads above these never swallow to null: the card
 * needs the server's `reason` on a 409/502 to say why in words. Resolves
 * { status, sent, ...body } for every answer; a request that never reached
 * the server resolves with reason 'unreachable'. Never throws.
 */
async function postToday(path, payload) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: payload === undefined ? undefined : JSON.stringify(payload),
    });
  } catch {
    return { status: 0, sent: false, reason: 'unreachable' };
  }
  if (res.status === 401) {
    // Same as request(): the session is gone — clear auth and reload.
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('tachoparser_user');
    window.location.reload();
    return { status: 401, sent: false, reason: 'session_expired' };
  }
  let body = {};
  try {
    body = await res.json();
  } catch {
    body = {};
  }
  if (!body || typeof body !== 'object') body = {};
  if (res.ok) return { status: res.status, ...body, sent: Boolean(body.sent) };
  return { status: res.status, ...body, sent: false, reason: body.reason || 'unknown' };
}

/**
 * POST /today/thanks — send the Worth-a-word thank-you to the driver's
 * phone through the driver app.
 */
export async function sendThanks(driverId) {
  return postToday('/today/thanks', { driver_id: driverId });
}

/**
 * POST /today/highfive — send the weekly yard high five to every clear
 * driver's phone through the driver app. No body: the server recomputes
 * the week and its clear drivers from the same read the card shows.
 */
export async function sendHighFive() {
  return postToday('/today/highfive');
}

/**
 * GET /api/card-expiry-watch — driver card renewals due (expired / <30d).
 * Server: { expired:[...], expiring_soon:[...], counts:{expired,expiring_soon} }
 */
export async function fetchCardExpiry() {
  try {
    return await getJson('/card-expiry-watch');
  } catch {
    return { expired: [], expiring_soon: [], counts: { expired: 0, expiring_soon: 0 } };
  }
}

/**
 * GET /api/auth/me — the current user + CURRENT org's er_enrolled flag. This is
 * the manager-callable source for the DVSA / Earned Recognition gate (the
 * admin-only /api/org/settings 403s for a normal manager). Returns just the
 * rule-policy slice the cockpit needs; fails soft to gated-off.
 * Server (relevant slice): { ..., er_enrolled: bool }
 */
export async function fetchOrgRulePolicy() {
  try {
    const me = await getJson('/auth/me');
    return { er_enrolled: Boolean(me && me.er_enrolled) };
  } catch {
    return { er_enrolled: false };
  }
}

/* ── Tacho-scope Discoveries (design 2026-08-08 §3/§5) ─────────────────── */

/**
 * GET /api/discoveries/card-vrns — card-sourced VRN discoveries for the
 * caller's org. status: 'active' (undisposed review queue) | 'qualified'
 * (disposed external/out_of_scope). Response: { vrns: [...],
 * missing_declaration: [...], status }.
 */
export async function fetchCardVrnDiscoveries(status = 'active') {
  return getJson(`/discoveries/card-vrns?status=${encodeURIComponent(status)}`);
}

/**
 * POST /api/vehicles/dispositions — record a §5 disposition for a card VRN.
 * disposition ∈ {'external', 'out_of_scope'}; admin-gated server-side.
 */
export async function setVrnDisposition(vrn, disposition, note) {
  const res = await request(`${BASE}/vehicles/dispositions`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ vrn, disposition, note: note || null }),
  });
  return res.json();
}

/** DELETE /api/vehicles/dispositions/{vrn} — re-qualify a disposed VRN. */
export async function deleteVrnDisposition(vrn) {
  const res = await request(
    `${BASE}/vehicles/dispositions/${encodeURIComponent(vrn)}`,
    { method: 'DELETE' },
  );
  return res.json();
}

/* ═══════════════════════════════════════════════════════════════
   Driver hours disciplinary process (Org Settings) —
   design 2026-09-11-disciplinary-policy-phase1 §5.

   The reads throw like every other read (the section shows "couldn't
   load"). The WRITES go through writeJson below, the postToday rule:
   never swallow to null, always resolve { ok, status, data, reason }
   so the walkthrough can say why in words (org_unmapped, no_recipients,
   ddir_unavailable, no_draft, invalid, unreachable …).
   ═══════════════════════════════════════════════════════════════ */

async function writeJson(method, path, payload) {
  let res;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: payload === undefined ? undefined : JSON.stringify(payload),
    });
  } catch {
    return { ok: false, status: 0, data: null, reason: 'unreachable' };
  }
  if (res.status === 401) {
    // Same as request(): the session is gone — clear auth and reload.
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('tachoparser_user');
    window.location.reload();
    return { ok: false, status: 401, data: null, reason: 'session_expired' };
  }
  let body = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  if (res.ok) return { ok: true, status: res.status, data: body, reason: null };
  // FastAPI puts a string or an object in `detail`; the design's 409s carry
  // `reason` at the top level or inside detail.
  const detail = body && typeof body === 'object' ? body.detail : null;
  // PUT draft 400 { detail: { errors: [...] } } names no reason: it is a
  // validation refusal, so it reads as 'invalid' and the errors are shown.
  const reason = (body && typeof body === 'object' && body.reason)
    || (detail && typeof detail === 'object' && detail.reason)
    || (detail && typeof detail === 'object' && Array.isArray(detail.errors) ? 'invalid' : null)
    || (typeof detail === 'string' ? detail : null)
    || 'unknown';
  return { ok: false, status: res.status, data: body, reason: String(reason) };
}

/**
 * GET /api/org/disciplinary-policy →
 *   { signed: row|null, draft: row|null, preset, walkthrough, alignment_for_preset,
 *     issue: { org_mapped: bool, recipients: int } }
 * Rows carry parsed `policy`, `alignment`, `summary`, and once sent
 * `issued_bulletin_id`, `issued_at`, `issued_to`. A draft row's `version`
 * is pre-assigned (previous signed + 1).
 */
export async function fetchDisciplinaryPolicy() {
  return getJson('/org/disciplinary-policy');
}

/** PUT /api/org/disciplinary-policy/draft { policy } → the draft row;
 *  400 { detail: { errors: [...] } } when the rules do not validate. */
export async function saveDisciplinaryPolicyDraft(policy) {
  return writeJson('PUT', '/org/disciplinary-policy/draft', { policy });
}

/** POST /api/org/disciplinary-policy/sign { method: 'identity' | 'paper' } → the signed row;
 *  409 { reason: 'no_draft' | 'invalid', errors? }. */
export async function signDisciplinaryPolicy(method = 'identity') {
  return writeJson('POST', '/org/disciplinary-policy/sign', { method });
}

/** POST /api/org/disciplinary-policy/issue (no body) →
 *  201 { sent, bid, recipients, skipped, issued_at, policy: the signed row }
 *  200 { sent, duplicate: true, bid }
 *  409 { reason: not_signed | org_unmapped | no_recipients | ddir_unknown_driver }
 *  502 { reason: ddir_unavailable }. */
export async function issueDisciplinaryPolicy() {
  return writeJson('POST', '/org/disciplinary-policy/issue');
}

/**
 * GET /api/org/disciplinary-policy/document[?draft=1][&paper=1] → the process
 * document as printable HTML (text, for a srcDoc iframe). `paper` adds the
 * print footer and signature block (design 2026-09-12 signed-copies-phase4
 * §3) for the paper-signing route.
 */
export async function fetchProcessDocumentHtml(draft = false, { paper = false } = {}) {
  const params = [draft ? 'draft=1' : '', paper ? 'paper=1' : ''].filter(Boolean).join('&');
  const res = await request(`${BASE}/org/disciplinary-policy/document${params ? `?${params}` : ''}`);
  return res.text();
}

/** PUT /api/org/settings/company { company_name, address } (admin). */
export async function saveCompanyDetails({ company_name, address }) {
  return writeJson('PUT', '/org/settings/company', { company_name, address });
}

/* ═══════════════════════════════════════════════════════════════
   Print and signed copies — design 2026-09-12-signed-copies-phase4
   §2, §3. One evidence store: a printed document, signed on paper,
   comes back as a file on the record it belongs to (a finding, a
   warning, a training, the process document). The reads fail soft
   to null (the control says "couldn't load", never "No signed copy
   yet" for a list that did not load); the writes throw with the
   server's `detail` in words, as the training writes do.
   ═══════════════════════════════════════════════════════════════ */

/**
 * POST /api/evidence (multipart) — upload one file as evidence on a
 * subject. `kind` is one of lib/evidence KINDS; `subject_type` is
 * 'infringement' | 'warning' | 'training' | 'policy'; `complete` (for a
 * training that is still assigned) marks it complete now by the uploader.
 * 201 { evidence: row }; 413 too large, 415 wrong type (sniffed from the
 * bytes), 404 foreign subject, 400 bad fields — each thrown with `detail`.
 * @returns {Promise<{ evidence: { id, kind, subject_type, subject_id, filename,
 *   content_type, size_bytes, uploaded_by, uploaded_at, note, status, url } }>}
 */
export async function uploadEvidence({ file, kind, subject_type, subject_id, note, complete } = {}) {
  if (!file) throw new Error('Choose a file first.');
  const body = new FormData();
  body.append('file', file, file.name || 'file');
  body.append('kind', String(kind || 'other'));
  body.append('subject_type', String(subject_type || ''));
  body.append('subject_id', String(subject_id ?? ''));
  const why = typeof note === 'string' ? note.trim() : '';
  if (why) body.append('note', why);
  if (complete) body.append('complete', '1');
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}/evidence`, { method: 'POST', body });
  return res.json();
}

/**
 * GET /api/evidence?subject_type=&subject_id= — the live files on one
 * subject. Resolves to null on any failure so the control can say
 * "couldn't load" rather than read as no copy on file.
 * @returns {Promise<{ evidence: Array<object> } | null>}
 */
export async function fetchEvidence(subjectType, subjectId) {
  if (!subjectType || subjectId === null || subjectId === undefined || subjectId === '') return null;
  try {
    const q = new URLSearchParams({ subject_type: String(subjectType), subject_id: String(subjectId) });
    return await getJson(`/evidence?${q.toString()}`);
  } catch {
    return null;
  }
}

/**
 * POST /api/evidence/{id}/remove { reason } — soft-remove a file (admin);
 * the file stays on disk, the row leaves the list. An empty reason is
 * refused here; the server's message is thrown on failure.
 */
export async function removeEvidence(id, reason) {
  const why = typeof reason === 'string' ? reason.trim() : '';
  if (!why) throw new Error('A reason is required to remove a file');
  return postJson(`/evidence/${encodeURIComponent(String(id))}/remove`, { reason: why });
}

/**
 * GET /api/evidence/{id}/file — open the bytes in a new tab. The route
 * needs the auth header, so it is fetched to a blob URL and opened from
 * there, never a bare <a href> (design §4). Throws with the server's
 * words when the file cannot be read; the blob URL is released after the
 * tab has had time to load it.
 */
export async function openEvidenceFile(id) {
  const res = await request(`${BASE}/evidence/${encodeURIComponent(String(id))}/file`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  // A same-origin blob needs no 'noopener' — and that feature makes
  // window.open return null, which would read as "blocked". Never fall
  // back to a same-tab navigation: that replaces the app (and the DDIR
  // embed) with the file.
  const tab = window.open(url, '_blank');
  if (!tab) throw new Error('The browser blocked the new tab. Allow pop-ups for this site and try again.');
  setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* released already */ } }, 60_000);
  return url;
}

/**
 * POST /api/infringements/{id}/printed { document: 'note' | 'closure' } —
 * stamp who printed a document for signature on paper and when (an
 * `infringement_actions` row, design §3). → { ok, printed_at }.
 */
export async function markPrinted(infringementId, document = 'note') {
  return postJson(`/infringements/${encodeURIComponent(String(infringementId))}/printed`, { document });
}

/* ═══════════════════════════════════════════════════════════════
   The document family — design 2026-09-12-document-family-phase5
   §4. Every step of the ladder has its own document, drafted by
   Andre and finished by the manager; stored so it re-prints
   identically; issued once. The list read fails soft to null (the
   card says "couldn't load", never "nothing applies" for a list that
   did not load); the writes throw with the server's `detail` in
   words, as the evidence writes do.
   ═══════════════════════════════════════════════════════════════ */

const docPath = (id, kind = '') =>
  `/infringements/${encodeURIComponent(String(id))}/documents${kind ? `/${encodeURIComponent(String(kind))}` : ''}`;

/**
 * GET /api/infringements/{id}/documents — the family for one finding.
 * Resolves to null on any failure.
 * @returns {Promise<{ documents: Array<{ kind, word, applies, status, issued_at,
 *   issued_by, document_id, signed_copies }>, stage, policy_version } | null>}
 */
export async function fetchDocuments(infringementId) {
  if (infringementId === null || infringementId === undefined || infringementId === '') return null;
  try {
    return await getJson(docPath(infringementId));
  } catch {
    return null;
  }
}

/**
 * GET /api/infringements/{id}/documents/{kind} — one document's fields
 * (the draft's, else Andre's), Andre's suggestion, the field specs when
 * the server sends them, its status and id. 404 for a kind that does
 * not apply — thrown with the server's words.
 * @returns {Promise<{ fields: object, suggested: object, specs?: Array<object>, status, document_id }>}
 */
export async function fetchDocument(infringementId, kind) {
  return getJson(docPath(infringementId, kind));
}

/**
 * PUT /api/infringements/{id}/documents/{kind} { fields } — save the
 * draft (manager). The server validates dates and enums; a 400 is
 * thrown with its `detail` in words. Returns the same shape as the read.
 */
export async function saveDocument(infringementId, kind, fields) {
  return putJson(docPath(infringementId, kind), { fields: fields && typeof fields === 'object' ? fields : {} });
}

/**
 * GET /api/infringements/{id}/documents/{kind}/html[?paper=1][&draft=1]
 * — the document as HTML text. `paper` adds the print footer and the
 * signature blocks; `draft` renders from the saved (or suggested)
 * fields even when an issued copy exists. The note and the closure
 * record route through it too. Fetched with the auth header, never a
 * bare link.
 */
export async function fetchDocumentHtml(infringementId, kind, { paper = false, draft = false } = {}) {
  const q = new URLSearchParams();
  if (paper) q.set('paper', '1');
  if (draft) q.set('draft', '1');
  const qs = q.toString();
  const res = await request(`${BASE}${docPath(infringementId, kind)}/html${qs ? `?${qs}` : ''}`);
  return res.text();
}

/**
 * POST /api/infringements/{id}/documents/{kind}/issue — freeze the
 * document (manager): its HTML is stored so it re-prints identically,
 * who issued it and when are stamped, an audit row is written.
 * → { document_id, status: 'issued', issued_at, issued_by }.
 */
export async function issueDocument(infringementId, kind) {
  return postJson(`${docPath(infringementId, kind)}/issue`, {});
}

/* ═══════════════════════════════════════════════════════════════
   Analogue charts — the TP-side reader (design 2026-09-18 §4 and
   §6). A manager photographs paper discs, TachoParser unwraps and
   reads each one, and the manager corrects the handwriting and
   confirms; the chart then becomes activity rows the engine judges.

   The upload goes in CHUNKS that share one batch id, because nginx
   caps a request body at 50M (nginx/nginx.conf) — ten to fifteen
   phone photographs, fewer than one scanner run — and its refusal is
   a raw proxy error page rather than a sentence the endpoint can
   write. lib/analogueCharts.planUploadChunks does the sizing; these
   wrappers only carry one group at a time.

   The reads throw with the server's `detail` in words, as the
   evidence family does, EXCEPT fetchOrgFeatures, which fails closed
   to {} — an org that cannot be asked does not get a screen that
   404s on its first click.
   ═══════════════════════════════════════════════════════════════ */

const chartPath = (chartId, tail = '') =>
  `/analogue-charts/${encodeURIComponent(String(chartId))}${tail}`;

/**
 * GET /api/org/features — which optional features this org has, read
 * once at app load to decide what the sidebar offers.
 * @returns {Promise<{ analogue_charts?: boolean, analogue_has_charts?: boolean }>}
 *   {} on ANY failure, which reads as "no optional features": the
 *   entitlement is the server's to grant, and a failed lookup must never
 *   open a door.
 */
export async function fetchOrgFeatures() {
  try {
    const body = await getJson('/org/features');
    return body && typeof body === 'object' && !Array.isArray(body) ? body : {};
  } catch {
    return {};
  }
}

/**
 * GET /api/analogue-charts[?review_status=] — every chart this org has,
 * newest first, org-scoped by the JWT and filtered by NOTHING else.
 *
 * In particular the server does not drop a chart whose driver has left:
 * paper arrives weeks late, so a leaver's disc is the ordinary backfill
 * case, and this codebase has a standing incident where an owned-only
 * filter silently hid most of a customer's fleet from them.
 *
 * @returns {Promise<{ charts: Array<object> } | Array<object>>}
 */
export async function fetchAnalogueCharts(params = {}) {
  return getJson(`/analogue-charts${buildQuery(params)}`);
}

/**
 * POST /api/analogue-charts (multipart) — ONE group of photographs.
 *
 * `batchId` joins this request to the rest of a scanner run. The upload
 * page makes one before the first group and sends it with every group,
 * the first included, so a run is one batch in the list, and a group sent
 * again after an answer that never came joins the same run: the server
 * gives back a disc it already has in that run rather than filing it a
 * second time. Left out, the server makes one of its own.
 *
 * `facts` is what the uploader typed for each disc of THIS group, in the
 * group's file order (lib/analogueFacts.factsForUpload shapes each one),
 * null for a disc with nothing typed. Leave it out, or pass null or [],
 * when nothing was typed for any disc. Anything else must be a list
 * EXACTLY as long as `files`, or this throws before anything is sent.
 *
 * It is never padded or cut to fit. A list that does not line up attaches
 * typed facts (driver, registration, odometers) to the wrong disc of a
 * legal record, and one padded or cut to fit would pass the server's own
 * check, which can only count the entries. The likely slip is the whole
 * run's facts handed to every chunk: cut to each chunk's length, the first
 * chunk's facts land on the second chunk's discs, and nothing after this
 * point could tell.
 *
 * 201 `{batch_id, charts:[{chart_id, original_filename, review_status}]}`.
 * The endpoint is all-or-nothing PER REQUEST, so a group that fails
 * leaves the groups already accepted exactly where they are.
 */
export async function uploadAnalogueCharts(files, batchId, facts) {
  const list = Array.from(files || []);
  if (!list.length) throw new Error('Choose at least one photograph.');
  /* Nothing typed is no argument, null or []. Anything else lines up with
     the photographs exactly, or nothing is sent (see above). */
  const typed = facts == null ? [] : facts;
  if (!Array.isArray(typed) || (typed.length > 0 && typed.length !== list.length)) {
    throw new Error('The typed facts do not line up with the photographs, so nothing was sent.');
  }
  const body = new FormData();
  for (const file of list) body.append('files', file, file.name || 'chart.jpg');
  const id = typeof batchId === 'string' ? batchId.trim() : '';
  if (id) body.append('batch_id', id);
  /* One entry per file, in file order, null for a disc with nothing typed
     (design 2026-09-25). Sent only when something was typed, so a plain
     stack goes up exactly as before. */
  const aligned = list.map((_, i) => typed[i] || null);
  if (aligned.some(Boolean)) body.append('facts', JSON.stringify(aligned));
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}/analogue-charts`, { method: 'POST', body });
  return res.json();
}

/**
 * GET /api/analogue-charts/pickers — the upload rows' drivers and
 * vehicles, `{drivers, vehicles}`. The drivers are the check screen's own
 * roster with no status filter, because paper arrives weeks late and a
 * leaver's disc is the ordinary backfill case. The vehicles leave out only
 * archived ones, analogue first.
 */
export async function fetchAnalogueChartPickers() {
  return getJson('/analogue-charts/pickers');
}

/**
 * GET /api/analogue-charts/{id} — one chart: the model's read, the
 * quality block, the warnings, the org's roster and `driver_suggestion`
 * (Task 12's review screen reads this).
 */
export async function fetchAnalogueChart(chartId) {
  return getJson(chartPath(chartId));
}

/**
 * POST /api/analogue-charts/{id}/confirm — the manager's CORRECTED
 * fields, which are what gets filed; the model's read is only ever a
 * starting point. Answers the receiver's eight-key envelope, whose
 * `driver_matched: false` means the chart is held and nothing was filed.
 */
export async function confirmAnalogueChart(chartId, fields) {
  return postJson(chartPath(chartId, '/confirm'), fields || {});
}

/**
 * POST /api/analogue-charts/{id}/correct — a FILED disc, corrected (design
 * 2026-09-30 B): Confirm's fields, with `new_vehicle`, and `reason`, what was
 * wrong with it as filed (lib/analogueReview's correctBody builds the body
 * with confirmBody, so the two doors send a disc the same way). The server
 * files it again as its next version and keeps the correction on the disc.
 *
 * Answers Confirm's eight-key envelope, whose `driver_matched: false` means
 * the correction is held and the disc is back to be checked, plus
 * `archived_vehicles` (the plates of the vehicles the disc left, archived
 * because a disc made each and nothing else is on file for it: [] when
 * none), `archived_vehicle` (the first of them, or null) and `corrections`
 * (the disc's list as the correction left it; the screen reads the
 * one-chart read's, not this). A 400 with the server's sentence for no
 * reason, nothing changed (on a disc no correction left part way), or a new
 * plate that is not one of the org's vehicles; a 409 for a disc from DDIR or
 * one that is not filed.
 */
export async function correctAnalogueChart(chartId, body) {
  return postJson(chartPath(chartId, '/correct'), body || {});
}

/** POST /api/analogue-charts/{id}/discard { reason } — rejected, nothing ingested. */
export async function discardAnalogueChart(chartId, reason) {
  const why = typeof reason === 'string' ? reason.trim() : '';
  if (!why) throw new Error('Say why the chart is being rejected.');
  return postJson(chartPath(chartId, '/discard'), { reason: why });
}

/**
 * POST /api/analogue-charts/{id}/requeue — read it again. Never
 * automatic: a retry that silently costs money is a surprise, so it is
 * always a manager pressing a button.
 */
export async function requeueAnalogueChart(chartId) {
  return postJson(chartPath(chartId, '/requeue'), {});
}

/**
 * POST /api/analogue-charts/{id}/reread — read a FILED disc again with the
 * current reader (design 2026-10-01). Free to the customer (Zed's pricing,
 * 2026-10-02), but a model call TachoParser pays for, so it is only ever a
 * manager pressing a button, as for Read again. The filed record stays
 * exactly as it is: the new read lands beside the disc, to be compared with
 * the filed trace and then filed (fileAnalogueReread) or not
 * (keepAnalogueReread).
 *
 * 200 `{chart_id, reread}`, the new re-read as the disc page shows it
 * (queued). Refused with the server's sentence: 409 for a disc from the DDIR
 * app, one not filed, one a filing left part way (correct it first), and
 * one whose re-read is already on its way or ready to compare; 403 when the
 * org's analogue charts are off; 404 for a disc not in this org; 503 when no
 * reader is configured.
 *
 * With `photo` (Zed, 2026-10-20: "Read again with a new photo"), the new
 * photograph of the disc is sent with the request, as multipart `photo`, and
 * the new read is made from it instead of the photo the disc was filed from.
 * The server checks it as the new-photo door does (its type, size and
 * pixels; a PDF is refused) and says why in its own words. Filing that new
 * read makes the new photo the disc's; keeping the filed one leaves the
 * disc's photo as it is. Without `photo`, exactly the request it always was.
 */
export async function rereadAnalogueChart(chartId, photo) {
  if (!photo) return postJson(chartPath(chartId, '/reread'), {});
  const body = new FormData();
  body.append('photo', photo, photo.name || 'chart.jpg');
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}${chartPath(chartId, '/reread')}`, { method: 'POST', body });
  return res.json();
}

/**
 * POST /api/analogue-charts/{id}/reread/{reread_id}/file — file the new read
 * as the disc's next version. TRACE ONLY (Zed's decision 1): the trace is the
 * new read's, and the plate, driver, dates, odometers and places stay as the
 * manager checked them. The re-read id names the read that was compared, so
 * nothing else is sent.
 *
 * Answers Confirm's eight-key envelope, whose `driver_matched: false` means
 * the disc is held and nothing was filed, plus `reread` (the re-read as the
 * filing left it) and `corrections` (the disc's history, which now holds the
 * new read's entry). Refused with the server's sentence: 409 for a re-read no
 * longer ready or a disc no longer filed, 400 for a new trace that cannot be
 * filed (the disc page's `reread.problem` says so before the click), 403 when
 * the org's analogue charts are off, and 404 for a disc or re-read not this
 * org's.
 */
export async function fileAnalogueReread(chartId, rereadId) {
  return postJson(chartPath(chartId, `/reread/${encodeURIComponent(String(rereadId))}/file`), {});
}

/**
 * POST /api/analogue-charts/{id}/reread/{reread_id}/keep — keep the filed
 * record and close the re-read: a new read declined, one the reader could not
 * use dismissed, or one still waiting for the reader called off. Nothing filed
 * changes.
 *
 * 200 `{reread}`, the re-read as kept. A 409 with the server's sentence while
 * the disc is being read again right now (keep the filed record once the read
 * is done) or once the re-read has moved on; 403 when the org's analogue
 * charts are off; 404 for a disc or re-read not this org's.
 */
export async function keepAnalogueReread(chartId, rereadId) {
  return postJson(chartPath(chartId, `/reread/${encodeURIComponent(String(rereadId))}/keep`), {});
}

/**
 * POST /api/analogue-charts/{id}/photo (multipart) — a new photograph for
 * a disc whose last one could not be read (rejected_quality or failed, the
 * other way out besides reading the same one again), or for one waiting to
 * be checked (needs_review, Decision B).
 *
 * What was typed for the disc at upload, its back and its place in its
 * scanner run are KEPT. The model's reading is cleared, with what a confirm
 * left on a disc confirmed before, and the disc is queued to be read
 * afresh: corrections made on the check screen are not kept. 409 for a disc
 * in any other state, for one whose confirm did not finish (half filed),
 * and for one that came from the DDIR app, which holds its own images.
 *
 * 200 `{chart_id, review_status}`.
 */
export async function replaceAnalogueChartPhoto(chartId, file) {
  if (!file) throw new Error('Choose a new photograph of the disc.');
  const body = new FormData();
  body.append('file', file, file.name || 'chart.jpg');
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}${chartPath(chartId, '/photo')}`, { method: 'POST', body });
  return res.json();
}

/**
 * POST /api/analogue-charts/{id}/back (multipart) — the reverse of the
 * disc, attached at review. Drivers write on the back when a day ran
 * over more than one vehicle or a hold-up needs explaining.
 *
 * ONE file, and A SECOND BACK REPLACES THE FIRST — re-taking a bad
 * photograph is precisely what a manager is doing at review, and the
 * row has one `back_storage_path`. The endpoint refuses (409) once the
 * chart has been confirmed or discarded, and refuses a chart that came
 * from the DDIR app, which holds its own images.
 *
 * 200 `{chart_id, back, replaced}`.
 */
export async function uploadAnalogueChartBack(chartId, file) {
  if (!file) throw new Error('Choose a photograph of the back of the disc.');
  const body = new FormData();
  body.append('file', file, file.name || 'back.jpg');
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}${chartPath(chartId, '/back')}`, { method: 'POST', body });
  return res.json();
}

/**
 * The URL of one face of a chart — 'image', 'back' or 'strip'. It is
 * NOT a bare <img src>: the endpoint is org-scoped through the JWT, so
 * the bytes are fetched with the auth header and handed to the page as
 * an object URL (the caller revokes it).
 *
 * `version` is optional, and the server reads nothing from it: it is
 * there only so the browser's cache sees a new URL when the picture
 * behind the old one has changed.
 */
export async function fetchAnalogueChartFace(chartId, face = 'image', version) {
  /* The faces are cached privately for five minutes, and a replaced photo or
     a re-read strip keeps its URL. The version (the photo's hash, the
     strip's read time) makes a new picture a new URL. */
  const v = version ? `?v=${encodeURIComponent(String(version))}` : '';
  const res = await request(`${BASE}${chartPath(chartId, `/${encodeURIComponent(String(face))}`)}${v}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

/* ═══════════════════════════════════════════════════════════════
   Analogue discs — disc problems (design 2026-10-06 §2, plan Tasks
   1.2 and 1.4). A manager's record of a disc that was lost, damaged,
   unreadable, missing because the tachograph was faulty, or never
   handed in by a driver who left: what happened, the photo of the
   driver's own signed note, and the outcome.

   The note goes WITH the record, in the same multipart request, and the
   server keeps both in one transaction: there is never a record without
   its note, nor a note without its record. None of these doors spends a
   model call, so they work whether the reader is switched on or off.
   They throw with the server's `detail` in words, as the chart doors do.
   ═══════════════════════════════════════════════════════════════ */

/* The record's fields, as the doors take them (_disc_problem_form). */
const DISC_PROBLEM_FIELDS = Object.freeze([
  'kind', 'driver_id', 'vehicle_registration', 'date_start', 'date_end', 'chart_id',
  'replacement_chart_id', 'repair_date', 'outcome', 'outcome_note', 'gap_key',
]);

/* The multipart body of a record: each field that holds something, and the
   photo of the driver's note when one was chosen. A field left blank is left
   out, which the server reads as absent: on an edit, as cleared, because an
   edit sends the whole record again (lib/missingDiscs.problemFields shapes
   it). */
function discProblemBody(fields, photo) {
  const body = new FormData();
  const f = fields && typeof fields === 'object' ? fields : {};
  for (const key of DISC_PROBLEM_FIELDS) {
    const value = typeof f[key] === 'string' ? f[key].trim() : '';
    if (value) body.append(key, value);
  }
  if (photo) body.append('photo', photo, photo.name || 'note.jpg');
  return body;
}

/**
 * GET /api/analogue-discs/problems[?from=&to=] — the org's current disc
 * problems, the newest disc first: `{problems: [...]}`, each with every
 * column, the driver's roster name (`driver_name`) and the driver's note as
 * the evidence store shows it (`evidence`, with its `url`, or null).
 * `from` / `to` ('YYYY-MM-DD') keep the records whose days overlap them.
 */
export async function fetchDiscProblems({ from, to } = {}) {
  const q = new URLSearchParams();
  if (typeof from === 'string' && from.trim()) q.set('from', from.trim());
  if (typeof to === 'string' && to.trim()) q.set('to', to.trim());
  const tail = q.toString();
  return getJson(`/analogue-discs/problems${tail ? `?${tail}` : ''}`);
}

/**
 * GET /api/analogue-discs/missing — the Missing discs section's first list
 * (admin): `{gaps, held, problems, counts}`, the odometer gaps between the
 * org's filed discs (yard tier included, an assigned gap still listed), the
 * possible gaps still waiting on a disc or a reading, and the readings to
 * put right. A gap is closed at the close door (closeMileage) by its own
 * gap_key, or explained by a disc problem that names it.
 *
 * `persist: false` asks for `?persist=0`: the same answer from the chain
 * walked in memory, nothing stored and no lock taken. The sidebar's count
 * uses it, as it asks on every app load.
 */
export async function fetchMissingDiscs({ persist = true } = {}) {
  return getJson(`/analogue-discs/missing${persist === false ? '?persist=0' : ''}`);
}

/**
 * POST /api/analogue-discs/problems (multipart) — record what happened to a
 * disc. `photo` is the driver's signed note, required for every kind but a
 * disc not handed in (the server says so with a 400 when it is missing).
 * 201 `{problem, superseded: null, rejected_chart, gap_closure,
 * disciplinary}`: an unreadable disc named by `chart_id` is rejected in the
 * same request, and a disciplinary outcome comes back as an OFFER of the
 * driver's page (`disciplinary.nav`), never started.
 */
export async function recordDiscProblem(fields, photo) {
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}/analogue-discs/problems`, {
    method: 'POST', body: discProblemBody(fields, photo),
  });
  return res.json();
}

/**
 * POST /api/analogue-discs/problems/{id}/supersede (multipart) — edit a disc
 * problem: the WHOLE record again, as at recordDiscProblem, written as a new
 * version of it; the version edited is kept. `photo` is optional: the
 * driver's note on file carries over without one while it is live. 409 once
 * another edit has replaced this version, in the server's words.
 */
export async function supersedeDiscProblem(problemId, fields, photo) {
  const path = `/analogue-discs/problems/${encodeURIComponent(String(problemId))}/supersede`;
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}${path}`, { method: 'POST', body: discProblemBody(fields, photo) });
  return res.json();
}

/* ═══════════════════════════════════════════════════════════════
   Analogue discs — the driver's manual entries (design 2026-10-06 §5,
   plan Tasks 3.1 and 3.2). The driver's own record, written on the back
   of a disc or on a temporary sheet, copied in as written with a photo
   of the original. The entries COUNT in the hours, so the photo goes
   WITH the batch, in the same multipart request: no photo, no entry.
   They throw with the server's `detail` in words, as the disc problem
   doors do (a refused driving entry says why).
   ═══════════════════════════════════════════════════════════════ */

/* The batch's multipart body (_manual_entries_form): the driver, the source,
   the disc when it is on one, the entries as a JSON array, and the photo. */
function manualEntriesBody({ driver_id, source, chart_id, entries } = {}, photo) {
  const body = new FormData();
  const put = (key, value) => {
    const text = typeof value === 'string' ? value.trim() : '';
    if (text) body.append(key, text);
  };
  put('driver_id', driver_id);
  put('source', source);
  put('chart_id', chart_id);
  body.append('entries', JSON.stringify(Array.isArray(entries) ? entries : []));
  if (photo) body.append('photo', photo, photo.name || 'original.jpg');
  return body;
}

/**
 * GET /api/analogue-discs/manual-entries[?driver_id=&from=&to=] — the org's
 * current manual entries by day and clock: `{entries: [...]}`, each with
 * its clock times, the photo of the original (`evidence`) and
 * `allowed_by_fault_gone` (driving whose tachograph fault is no longer on
 * record).
 */
export async function fetchManualEntries({ driverId, from, to } = {}) {
  const q = new URLSearchParams();
  if (typeof driverId === 'string' && driverId.trim()) q.set('driver_id', driverId.trim());
  if (typeof from === 'string' && from.trim()) q.set('from', from.trim());
  if (typeof to === 'string' && to.trim()) q.set('to', to.trim());
  const tail = q.toString();
  return getJson(`/analogue-discs/manual-entries${tail ? `?${tail}` : ''}`);
}

/**
 * POST /api/analogue-discs/manual-entries (multipart) — record a batch of
 * one driver's entries from one source: `back_of_disc` (with `chart_id`) or
 * `temporary_sheet`. `photo` is REQUIRED. 201 `{entries, superseded: null,
 * rebuild}`; 400 / 409 in the server's words (driving without a fault, an
 * overlap, a day off the disc).
 */
export async function recordManualEntries(fields, photo) {
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}/analogue-discs/manual-entries`, {
    method: 'POST', body: manualEntriesBody(fields, photo),
  });
  return res.json();
}

/**
 * POST /api/analogue-discs/manual-entries/{id}/supersede (multipart) — edit
 * one entry: the whole entry again (exactly one), written as a new version;
 * the version edited is kept. `photo` is optional: the original carries over
 * while it is live.
 */
export async function supersedeManualEntry(entryId, fields, photo) {
  const path = `/analogue-discs/manual-entries/${encodeURIComponent(String(entryId))}/supersede`;
  // No Content-Type header: the browser sets the multipart boundary.
  const res = await request(`${BASE}${path}`, { method: 'POST', body: manualEntriesBody(fields, photo) });
  return res.json();
}

/**
 * POST /api/analogue-discs/manual-entries/{id}/withdraw — withdraw one entry
 * typed in error, `{reason}` REQUIRED. The entry stops counting; its record
 * is kept with the reason.
 */
export async function withdrawManualEntry(entryId, reason) {
  const path = `/analogue-discs/manual-entries/${encodeURIComponent(String(entryId))}/withdraw`;
  return postJson(path, { reason: typeof reason === 'string' ? reason.trim() : '' });
}
